import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiCalendar, FiCheckCircle, FiCreditCard, FiFileText, FiFlag, FiMail, FiMapPin, FiPhone, FiShield, FiUser } from 'react-icons/fi';
import { useNotification } from '../context/NotificationContext';
import { useAuth, type User } from '../context/AuthContext';
import { http } from '../api/http';
import { BankDetailsSection } from './BankDetailsPage';
import { KycDocumentsSection } from './ProfilePage';
import { BankService } from '../services/bank.service';
import { ProfileService } from '../services/profile.service';
import { hasAllRequiredKycDocs } from '../utils/kycDocuments';
import styles from './CompleteProfilePage.module.css';

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone_e164: string;
  rfc: string;
  country: string;
  state: string;
  city: string;
  address: string;
  postal_code: string;
  date_of_birth: string;
  nationality: string;
}

type OnboardingStep = 'personal' | 'bank' | 'kyc';

const CompleteProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { checkProfileStatus, user } = useAuth();

  const [formData, setFormData] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    email: '',
    phone_e164: '',
    rfc: '',
    country: 'México',
    state: '',
    city: '',
    address: '',
    postal_code: '',
    date_of_birth: '',
    nationality: 'Mexicana'
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(user);
  const [step, setStep] = useState<OnboardingStep>('personal');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [dataConsentAccepted, setDataConsentAccepted] = useState(false);

  const resolveRedirect = (role?: string | null) => {
    if (role === 'developer') return '/developer/panel';
    if (role === 'admin' || role === 'manager') return '/admin/dashboard';
    return '/dashboard';
  };

  const resolveInitialStep = async (status: any): Promise<OnboardingStep | 'done'> => {
    if (!status?.profile_completed) return 'personal';

    const [bankDetails, kycDocuments] = await Promise.all([
      BankService.getMyBankDetails().catch(() => null),
      ProfileService.getKycDocuments().catch(() => ({ items: [] }))
    ]);

    if (!bankDetails?.clabe) return 'bank';
    if (!hasAllRequiredKycDocs(kycDocuments.items || [])) return 'kyc';
    return 'done';
  };

  useEffect(() => {
    let isMounted = true;

    const initializeProfile = async () => {
      setInitialLoading(true);
      try {
        const [profile, status] = await Promise.all([
          http<User>('/me'),
          http<any>('/me/profile/status')
        ]);
        if (!isMounted) return;

        setCurrentUser(profile);
        setUserRole(profile.role || null);
        setProfileStatus(status);
        setFormData(prev => ({
          ...prev,
          email: profile.email || '',
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          phone_e164: (profile as any).phone_e164 || profile.phone || '',
          country: profile.country || 'México',
          state: profile.state || '',
          city: profile.city || '',
          address: profile.address || '',
          postal_code: profile.postal_code || '',
          date_of_birth: profile.date_of_birth || '',
          nationality: profile.nationality || 'Mexicana',
          rfc: profile.rfc || ''
        }));

        const initialStep = await resolveInitialStep(status);
        if (!isMounted) return;

        if (initialStep === 'done') {
          navigate(resolveRedirect(profile.role || user?.role), { replace: true });
          return;
        }

        setStep(initialStep);
      } catch (error: any) {
        if (!isMounted) return;
        
        console.error('Error initializing profile:', error);
        const status = error?.status || error?.payload?.statusCode;
        
        // Si es 401 o 403, redirigir al login
        if (status === 401 || status === 403) {
          addNotification('Sesión expirada. Por favor, inicia sesión nuevamente.', 'error');
          navigate('/login', { replace: true });
        } else if (status === 404) {
          addNotification('Endpoint no encontrado. Por favor, contacta al administrador.', 'error');
        } else {
          addNotification('Error al cargar el perfil. Inténtalo de nuevo.', 'error');
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    initializeProfile();

    return () => {
      isMounted = false;
    };
  }, []); // Dependencias vacías - solo se ejecuta una vez al montar

  useEffect(() => {
    if (user) setCurrentUser(user);
  }, [user]);

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.first_name.trim()) errors.push('Nombre es requerido');
    if (!formData.last_name.trim()) errors.push('Apellido es requerido');
    if (!formData.date_of_birth) errors.push('Fecha de nacimiento es requerida');
    if (!formData.nationality.trim()) errors.push('Nacionalidad es requerida');
    if (!formData.phone_e164.trim()) errors.push('Teléfono es requerido');
    if (!formData.country.trim()) errors.push('País es requerido');
    if (!formData.state.trim()) errors.push('Estado es requerido');
    if (!formData.city.trim()) errors.push('Ciudad es requerida');
    if (!formData.postal_code.trim()) errors.push('Código postal es requerido');
    if (!formData.address.trim()) errors.push('Dirección completa es requerida');
    if (!formData.rfc.trim()) errors.push('RFC es requerido');
    if (!privacyAccepted) errors.push('Debes aceptar el Aviso de Privacidad');
    if (!dataConsentAccepted) errors.push('Debes aceptar el consentimiento de datos');

    // Validar edad mínima
    if (formData.date_of_birth) {
      const birthDate = new Date(formData.date_of_birth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        errors.push('Debes ser mayor de 18 años');
      }
    }

    // Validar formato de RFC
    if (formData.rfc && !/^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/.test(formData.rfc.toUpperCase())) {
      errors.push('Formato de RFC inválido');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      addNotification(errors[0], 'error');
      return;
    }

    setLoading(true);
    
    try {
      // Excluir el email del body ya que es readonly y no se puede modificar
      const { email, ...profileDataToSend } = formData;
      
      await http('/me/profile', {
        method: 'PUT',
        body: profileDataToSend
      });

      // Forzar actualización inmediata del estado del perfil
      await checkProfileStatus(true);
      const status = await http<any>('/me/profile/status').catch(() => null);
      if (status) setProfileStatus(status);

      addNotification('Datos personales guardados. Ahora agrega tu cuenta bancaria.', 'success');
      setStep('bank');
    } catch (error: any) {
      const message = error?.payload?.message || 'Error al completar perfil';
      addNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBankContinue = async () => {
    setLoading(true);
    try {
      const bankDetails = await BankService.getMyBankDetails();
      if (!bankDetails?.clabe) {
        addNotification('Agrega y guarda una cuenta bancaria antes de continuar.', 'error');
        return;
      }

      addNotification('Cuenta bancaria registrada. Ahora sube tus documentos KYC.', 'success');
      setStep('kyc');
    } catch {
      addNotification('No se pudieron validar tus datos bancarios. Inténtalo de nuevo.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFinishKyc = async () => {
    setLoading(true);
    try {
      const docs = await ProfileService.getKycDocuments();
      if (!hasAllRequiredKycDocs(docs.items || [])) {
        addNotification('Sube los tres documentos KYC requeridos antes de continuar.', 'error');
        return;
      }

      await checkProfileStatus(true);
      addNotification('Registro completado. Tus documentos quedaron en revisión.', 'success');
      navigate(resolveRedirect(userRole || currentUser?.role || user?.role), { replace: true });
    } catch {
      addNotification('No se pudieron validar tus documentos KYC. Inténtalo de nuevo.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = step === 'personal' ? 1 : step === 'bank' ? 2 : 3;
  const stepProgress = Math.round((stepIndex / 3) * 100);
  const headerIcon = step === 'personal' ? <FiUser /> : step === 'bank' ? <FiCreditCard /> : <FiShield />;
  const headerTitle = step === 'personal'
    ? 'Completar Perfil'
    : step === 'bank'
      ? 'Agregar cuenta bancaria'
      : 'Agregar documentos KYC';
  const headerSubtitle = step === 'personal'
    ? 'Ingresa tu información personal para continuar con el proceso de verificación'
    : step === 'bank'
      ? 'Registra tu cuenta bancaria antes de entrar al dashboard'
      : 'Sube tus documentos oficiales para terminar el alta inicial';

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FiUser />
            </div>
            <div className={styles.headerText}>
              <h1 className={styles.title}>Verificando perfil...</h1>
              <p className={styles.subtitle}>
                Cargando tu información
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className={styles.spinner}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            {headerIcon}
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.title}>{headerTitle}</h1>
            <p className={styles.subtitle}>
              {headerSubtitle}
            </p>
          </div>
        </div>

        <div className={styles.stepper}>
          <div className={`${styles.stepItem} ${stepIndex >= 1 ? styles.stepItemActive : ''}`}>
            <span>1</span>
            Datos personales
          </div>
          <div className={`${styles.stepItem} ${stepIndex >= 2 ? styles.stepItemActive : ''}`}>
            <span>2</span>
            Datos bancarios
          </div>
          <div className={`${styles.stepItem} ${stepIndex >= 3 ? styles.stepItemActive : ''}`}>
            <span>3</span>
            KYC
          </div>
        </div>

        {(profileStatus || stepProgress) && (
          <div className={styles.progressBar}>
            <div className={styles.progressLabel}>
              Progreso de alta: {stepProgress}%
            </div>
            <div className={styles.progressTrack}>
              <div 
                className={styles.progressFill}
                style={{ width: `${stepProgress}%` }}
              />
            </div>
          </div>
        )}

        {step === 'personal' ? (
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Información Personal */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FiUser /> Información Personal
            </h3>
            
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>
                  Nombre(s) <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className={styles.input}
                  placeholder="Tu nombre"
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Apellido <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className={styles.input}
                  placeholder="Tu apellido"
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>
                  <FiCalendar /> Fecha de Nacimiento <span className={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  <FiFlag /> Nacionalidad <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e) => handleInputChange('nationality', e.target.value)}
                  className={styles.input}
                  placeholder="Ej: Mexicana"
                  required
                />
              </div>
            </div>
          </div>

          {/* Datos de Contacto */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FiMail /> Datos de Contacto
            </h3>
            
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>
                  <FiPhone /> Teléfono <span className={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone_e164}
                  onChange={(e) => handleInputChange('phone_e164', e.target.value)}
                  className={styles.input}
                  placeholder="+52 55 1234 5678"
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  <FiMail /> Correo (solo lectura)
                </label>
                <input
                  type="email"
                  value={formData.email}
                  className={`${styles.input} ${styles.readonlyField}`}
                  readOnly
                  disabled
                />
                <small className={styles.hint}>
                  El correo no se puede cambiar ya que es tu clave de acceso
                </small>
              </div>
            </div>
          </div>

          {/* Domicilio */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FiMapPin /> Domicilio
            </h3>
            
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>
                  País <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className={styles.input}
                  placeholder="México"
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Estado <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={styles.input}
                  placeholder="Ciudad de México"
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>
                  Ciudad <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className={styles.input}
                  placeholder="Ciudad de México"
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Código Postal <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e.target.value)}
                  className={styles.input}
                  placeholder="03100"
                  maxLength={5}
                  required
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                Dirección Completa <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={styles.input}
                placeholder="Calle y número, colonia"
                required
              />
            </div>
          </div>

          {/* Información Fiscal */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FiFileText /> Información Fiscal
            </h3>
            
            <div className={styles.field}>
              <label className={styles.label}>
                RFC <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.rfc}
                onChange={(e) => handleInputChange('rfc', e.target.value.toUpperCase())}
                className={styles.input}
                placeholder="ABCD123456XYZ"
                maxLength={13}
                required
              />
              <small className={styles.hint}>
                Registro Federal de Contribuyentes (13 caracteres)
              </small>
            </div>
          </div>

          {/* Políticas y Consentimientos */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FiShield /> Políticas y Consentimientos
            </h3>
            
            <div className={styles.policiesGrid}>
              <div className={styles.policyItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    required
                  />
                  <span className={styles.policyText}>
                    Acepto el <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.policyLink}>Aviso de Privacidad</a> <span className={styles.required}>*</span>
                  </span>
                </label>
              </div>
              
              <div className={styles.policyItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={dataConsentAccepted}
                    onChange={(e) => setDataConsentAccepted(e.target.checked)}
                    required
                  />
                  <span className={styles.policyText}>
                    Doy mi consentimiento para el uso y resguardo de mis datos personales, validación de identidad y consultas ante proveedores externos <span className={styles.required}>*</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="submit"
              disabled={loading}
              className={styles.submitButton}
            >
              {loading ? 'Guardando...' : 'Completar Perfil'}
            </button>
          </div>
        </form>
        ) : null}

        {step === 'bank' ? (
          <div className={styles.embeddedStep}>
            <BankDetailsSection />
            <div className={styles.actions}>
              <button
                type="button"
                disabled={loading}
                className={styles.submitButton}
                onClick={handleBankContinue}
              >
                {loading ? 'Validando...' : 'Continuar a KYC'} <FiArrowRight />
              </button>
            </div>
          </div>
        ) : null}

        {step === 'kyc' && (currentUser || user) ? (
          <div className={styles.embeddedStep}>
            <KycDocumentsSection user={(currentUser || user) as User} />
            <div className={styles.actions}>
              <button
                type="button"
                disabled={loading}
                className={styles.submitButton}
                onClick={handleFinishKyc}
              >
                {loading ? 'Validando...' : 'Finalizar y entrar al dashboard'} <FiCheckCircle />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CompleteProfilePage;
